import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, File, X, Check, AlertCircle, Image, FileText, Video, Info } from 'lucide-react'
import { ContentMetadataForm } from '../components/ContentMetadataForm'

interface UploadedFile {
  id: string
  originalName: string
  fileName: string
  mimeType: string
  category: string
  size: number
  path: string
  thumbnailPath: string | null
  uploadedAt: string
}

export function ContentManagement() {
  const [files, setFiles] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  // State for metadata form
  const [showMetadataForm, setShowMetadataForm] = useState(false)
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null)

  // Get access token from localStorage
  const accessToken = localStorage.getItem('token')

  // Get user from localStorage for role check
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  // Check if user has permission (company admin or super admin)
  const canUpload = user && (user.role === 'company_admin' || user.role === 'super_admin')

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="w-6 h-6" />
    if (mimeType.startsWith('video/')) return <Video className="w-6 h-6" />
    if (mimeType.includes('pdf') || mimeType.includes('text') || mimeType.includes('json')) {
      return <FileText className="w-6 h-6" />
    }
    return <File className="w-6 h-6" />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024 // 50MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 50MB limit' }
    }

    // Check file type (whitelist approach)
    const allowedTypes = [
      'application/json',
      'text/tsx',
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'video/mp4',
      'video/webm',
      'video/ogg',
      'text/markdown',
      'text/plain'
    ]

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: `File type ${file.type} is not allowed` }
    }

    return { valid: true }
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    processFiles(droppedFiles)
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    processFiles(selectedFiles)
  }, [])

  const processFiles = (newFiles: File[]) => {
    // Validate all files
    const validFiles: File[] = []
    const errors: string[] = []

    newFiles.forEach(file => {
      const validation = validateFile(file)
      if (validation.valid) {
        validFiles.push(file)
      } else {
        errors.push(`${file.name}: ${validation.error}`)
      }
    })

    if (errors.length > 0) {
      setUploadStatus({
        type: 'error',
        message: errors.join('; ')
      })
      setTimeout(() => setUploadStatus({ type: null, message: '' }), 5000)
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles])
    }
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const uploadFiles = async () => {
    if (files.length === 0 || !accessToken) {
      setUploadStatus({
        type: 'error',
        message: accessToken ? 'No files to upload' : 'Not authenticated'
      })
      setTimeout(() => setUploadStatus({ type: null, message: '' }), 5000)
      return
    }

    setUploading(true)
    setUploadProgress(0)
    setUploadStatus({ type: null, message: '' })

    try {
      // Upload files one by one
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch('http://localhost:3000/api/content/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          },
          body: formData
        })

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.message || 'Upload failed')
        }

        const data = await response.json()
        return data.file
      })

      const uploaded = await Promise.all(uploadPromises)

      setUploadedFiles(prev => [...uploaded.reverse(), ...prev])
      setFiles([])
      setUploadProgress(100)

      setUploadStatus({
        type: 'success',
        message: `Successfully uploaded ${uploaded.length} file${uploaded.length > 1 ? 's' : ''}`
      })

      setTimeout(() => setUploadStatus({ type: null, message: '' }), 5000)
    } catch (error: any) {
      setUploadStatus({
        type: 'error',
        message: error.message || 'Failed to upload files'
      })
      setTimeout(() => setUploadStatus({ type: null, message: '' }), 5000)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  if (!canUpload) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have permission to upload content. Only company admins and super admins can upload files.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Upload Zone */}
      <motion.div
        className={`mb-8 border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50 hover:bg-primary/5'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <Upload className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">
          {isDragging ? 'Drop files here' : 'Drag and drop files here'}
        </h3>
        <p className="text-muted-foreground mb-4">or</p>
        <label className="inline-block">
          <input
            type="file"
            multiple
            accept=".json,.tsx,.pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.webm,.ogv,.md,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          <span className="px-6 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors">
            Browse Files
          </span>
        </label>
        <p className="text-sm text-muted-foreground mt-4">
          Maximum file size: 50MB
        </p>
      </motion.div>

      {/* Status Messages */}
      <AnimatePresence>
        {uploadStatus.type && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              uploadStatus.type === 'success'
                ? 'bg-green-500/10 text-green-600 border border-green-500/20'
                : 'bg-red-500/10 text-red-600 border border-red-500/20'
            }`}
          >
            {uploadStatus.type === 'success' ? (
              <Check className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p>{uploadStatus.message}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Files to Upload */}
      {files.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h3 className="text-xl font-semibold mb-4">Files to Upload ({files.length})</h3>
          <div className="space-y-3">
            {files.map((file, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border"
              >
                <div className="text-primary">
                  {getFileIcon(file.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(file.size)} • {file.type || 'Unknown type'}
                  </p>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-2 hover:bg-destructive/10 rounded-lg transition-colors text-destructive"
                  disabled={uploading}
                >
                  <X className="w-5 h-5" />
                </button>
              </motion.div>
            ))}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <motion.button
            onClick={uploadFiles}
            disabled={uploading}
            className="mt-4 w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: uploading ? 1 : 1.02 }}
            whileTap={{ scale: uploading ? 1 : 0.98 }}
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
          </motion.button>
        </motion.div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && !showMetadataForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h3 className="text-xl font-semibold mb-4">Recently Uploaded ({uploadedFiles.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {uploadedFiles.map((file) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 bg-card rounded-lg border border-border hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-primary flex-shrink-0">
                    {getFileIcon(file.mimeType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm">{file.originalName}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>

                {/* Thumbnail for images */}
                {file.thumbnailPath && (
                  <div className="mb-3 rounded-lg overflow-hidden bg-secondary">
                    <img
                      src={`http://localhost:3000${file.thumbnailPath}`}
                      alt={file.originalName}
                      className="w-full h-32 object-cover"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between text-xs mb-3">
                  <span className="px-2 py-1 bg-secondary rounded">
                    {file.category}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(file.uploadedAt).toLocaleDateString()}
                  </span>
                </div>

                {/* Add Metadata Button */}
                <motion.button
                  onClick={() => {
                    setSelectedFile(file)
                    setShowMetadataForm(true)
                  }}
                  className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Info className="w-4 h-4" />
                  Add Metadata
                </motion.button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Metadata Form */}
      <AnimatePresence>
        {showMetadataForm && selectedFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mb-8"
          >
            <ContentMetadataForm
              fileId={selectedFile.id}
              fileType={selectedFile.category}
              filePath={selectedFile.path}
              thumbnailUrl={selectedFile.thumbnailPath}
              onSave={() => {
                setShowMetadataForm(false)
                setSelectedFile(null)
              }}
              onCancel={() => {
                setShowMetadataForm(false)
                setSelectedFile(null)
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
